import * as pdfjsLib from 'pdfjs-dist';
import { PDF_EXTRACTION_PAGE_COUNT } from './RRDPdfConfig';

// Point the worker at the static copy in /public so CRA can serve it without ejecting.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

// ── PDF → base64 page images ──────────────────────────────────────────────────
export type PdfImageUrlEntry = {
  type: 'image_url';
  image_url: {
    url: string;
  };
};

export type PdfValidationOptions = {
  deep?: boolean;
};

export type PdfValidationResult = {
  isValid: boolean;
  error?: string;
  pageCount?: number;
};

const fileBufferCache = new WeakMap<File, Promise<ArrayBuffer>>();
const pageImageCache = new WeakMap<File, Map<string, Promise<PdfImageUrlEntry[]>>>();

/** Reads a File into an ArrayBuffer. Shared by every PDF entry point below. */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  const cached = fileBufferCache.get(file);
  if (cached) return cached;

  const bufferPromise = new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
  fileBufferCache.set(file, bufferPromise);
  bufferPromise.catch(() => fileBufferCache.delete(file));
  return bufferPromise;
}

/**
 * Reads a PDF file, renders each page to its own image, base64-encodes each
 * page individually, and returns an array of image_url entries — one per page.
 * Pages are NOT combined into a single image.
 */
export async function convertPDFPagesToBase64Json(
  file: File,
  scale = 2.0,
  maxPages = PDF_EXTRACTION_PAGE_COUNT
): Promise<PdfImageUrlEntry[]> {
  const cacheKey = `${scale}:${maxPages}`;
  const cachedImages = pageImageCache.get(file)?.get(cacheKey);
  if (cachedImages) return cachedImages;

  const conversionPromise = convertPDFPages(file, scale, maxPages);
  const fileCache = pageImageCache.get(file) || new Map<string, Promise<PdfImageUrlEntry[]>>();
  fileCache.set(cacheKey, conversionPromise);
  pageImageCache.set(file, fileCache);
  conversionPromise.catch(() => fileCache.delete(cacheKey));
  return conversionPromise;
}

async function convertPDFPages(
  file: File,
  scale: number,
  maxPages: number,
): Promise<PdfImageUrlEntry[]> {
  const validation = await validatePDFFile(file, { deep: false });
  if (!validation.isValid) throw new Error(validation.error || 'The selected file is not a valid PDF.');

  const buffer = await readFileAsArrayBuffer(file);
  const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0) });
  const pdf = await loadingTask.promise;

  // Browsers cap canvas size (max dimension ~4096–32767 and total area
  // ~16.7M px on Safari). Downscale each page so it stays within safe limits,
  // otherwise toDataURL() can silently return "data:," (a blank image).
  const MAX_DIMENSION = 4096;
  const MAX_AREA = 16_777_216; // 4096 * 4096

  // One image_url entry per page.
  const entries: PdfImageUrlEntry[] = [];
  const pageCountToProcess = Math.min(pdf.numPages, Math.max(1, maxPages));

  for (let pageNum = 1; pageNum <= pageCountToProcess; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    const dimensionFactor = Math.min(
      1,
      MAX_DIMENSION / viewport.width,
      MAX_DIMENSION / viewport.height
    );
    const areaFactor = Math.min(
      1,
      Math.sqrt(MAX_AREA / (viewport.width * viewport.height))
    );
    const fit = Math.min(dimensionFactor, areaFactor);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width * fit));
    canvas.height = Math.max(1, Math.floor(viewport.height * fit));
    const ctx = canvas.getContext('2d')!;
    // White background so transparent areas don't turn black in JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const renderViewport = page.getViewport({ scale: scale * fit });
    await page.render({ canvasContext: ctx, canvas, viewport: renderViewport }).promise;
    page.cleanup();

    const url = canvas.toDataURL('image/jpeg');
    if (!url || url === 'data:,') {
      await pdf.destroy();
      throw new Error(`Failed to encode page ${pageNum} to an image (the rendered canvas was empty).`);
    }

    entries.push({
      type: 'image_url',
      image_url: {
        url,
      },
    });
  }

  await pdf.destroy();

  if (entries.length === 0) {
    throw new Error('PDF produced no renderable pages.');
  }

  return entries;
}

// PDF validation function
export async function validatePDFFile(
  file: File,
  options: PdfValidationOptions = { deep: true },
): Promise<PdfValidationResult> {
  if (file.size === 0) {
    return { isValid: false, error: 'The file is empty.' };
  }
  if (file.size < 100) {
    return { isValid: false, error: 'File is too small to be a valid PDF.' };
  }

  let buffer: ArrayBuffer;
  try {
    buffer = await readFileAsArrayBuffer(file);
  } catch (err: any) {
    return { isValid: false, error: err?.message || 'Failed to read the file.' };
  }

  const bytes = new Uint8Array(buffer);

  if (
    bytes[0] !== 0x25 || bytes[1] !== 0x50 || bytes[2] !== 0x44 ||
    bytes[3] !== 0x46 || bytes[4] !== 0x2D
  ) {
    return { isValid: false, error: 'File is not a valid PDF (missing PDF header). It may be corrupt or incorrectly named.' };
  }

  const tailStart = Math.max(0, bytes.length - 1024);
  const tail = bytes.slice(tailStart);
  let eofFound = false;
  for (let i = 0; i <= tail.length - 5; i++) {
    if (
      tail[i] === 0x25 && tail[i + 1] === 0x25 &&
      tail[i + 2] === 0x45 && tail[i + 3] === 0x4F && tail[i + 4] === 0x46
    ) {
      eofFound = true;
      break;
    }
  }

  if (!eofFound) {
    return { isValid: false, error: 'PDF is incomplete or corrupt (missing EOF marker). The file may be truncated and could have missing pages.' };
  }

  if (options.deep === false) {
    return { isValid: true };
  }

  const CORRUPT_IMAGE_PATTERNS = [
    'unable to decode image',
    'jpegerror',
    'unexpected marker',
    'end of the image data without finding an eoi',
    'unsupported predictor',
    'image decode',
    'insufficient data for an image',
    'insufficient data',
    'dependent image isn',
  ];

  try {
    const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0), stopAtErrors: true });
    const pdf = await loadingTask.promise;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const capturedMessages: string[] = [];
      const originalWarn = console.warn;
      const originalError = console.error;
      const capture = (...args: any[]) => {
        const msg = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ');
        capturedMessages.push(msg);
      };
      console.warn = (...args: any[]) => {
        capture(...args);
        originalWarn.apply(console, args);
      };
      console.error = (...args: any[]) => {
        capture(...args);
        originalError.apply(console, args);
      };

      const restoreConsole = () => {
        console.warn = originalWarn;
        console.error = originalError;
      };

      try {
        const page = await pdf.getPage(pageNum);
        await page.getOperatorList();

        // Render the page to a canvas to force image decoding. Some image
        // corruption (e.g. "insufficient data for an image") only surfaces
        // during actual rasterization, not while building the operator list.
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, canvas, viewport }).promise;

        page.cleanup();
      } catch (pageErr: any) {
        restoreConsole();
        await pdf.destroy();
        const msg = pageErr?.message || String(pageErr);
        return {
          isValid: false,
          error: `Page ${pageNum} contains a corrupt or unreadable image: ${msg}`,
        };
      } finally {
        restoreConsole();
      }

      const imageWarning = capturedMessages.find((w) =>
        CORRUPT_IMAGE_PATTERNS.some((pattern) => w.toLowerCase().includes(pattern))
      );
      if (imageWarning) {
        await pdf.destroy();
        return {
          isValid: false,
          error: `Page ${pageNum} contains a corrupt or unreadable image: ${imageWarning.replace(/^(?:Warning|Error):\s*/i, '')}`,
        };
      }
    }

    const pageCount = pdf.numPages;
    await pdf.destroy();
    return { isValid: true, pageCount };
  } catch (docErr: any) {
    const msg = docErr?.message || String(docErr);
    return { isValid: false, error: `Could not open PDF for validation: ${msg}` };
  }

}

/**
 * Returns the number of pages in a PDF file without full validation.
 * Assumes the file has already been validated.
 */
export async function getPDFPageCount(file: File): Promise<number> {
  const buffer = await readFileAsArrayBuffer(file);
  const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0) });
  const pdf = await loadingTask.promise;
  const count = pdf.numPages;
  await pdf.destroy();
  return count;
}
